class MHeader extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      headers: ["header"],
      logos: [".m-logo"],
      topbar: ".m-topbar",
      headerWrapper: ".m-header__wrapper",
    };
    this.domNodes = queryDomNodes(this.selectors, this);
    this.innerWidth = window.innerWidth;
    this.headerOffsetTop = 0;
    this.headerHeight = 0;
    this._stickyExtraSpace = 20;
    this.stickyHeader = this.dataset.sticky || "none";
    this._stickyScrollHandler = null;
    this._resizeHandler = null;
    this._resizeFrame = null;
    this._matchMobileHandler = null;
    this._unmatchMobileHandler = null;
    this._topbarObserver = null;
    this._topbarObserverFrame = null;
    this._topbarElement = null;
    this._topbarClickHandler = null;
    this._documentClickHandler = null;

    this.classes = {
      scrollUp: "scroll-up",
      scrollDown: "scroll-down",
      stuck: "stuck",
      always: "header-sticky-always",
      headerScrollUp: "header-scroll-up",
      headerScrollDown: "header-scroll-down",
      topbarLanguageOpen: "m-topbar-language-open",
    };
    this.init();
  }

  init() {
    this.transparentHeader =
      this.domNodes && this.domNodes.headers[0] && this.domNodes.headers[0].dataset.transparent === "true";
    this.refreshHeaderMetrics();
    this.initAddon();
    this.initTopbarControls();
    this.handleSticky();

    this._matchMobileHandler = () => {
      this.decorateTopbarControls();
      this.handleSticky();
    };
    this._unmatchMobileHandler = () => {
      this.decorateTopbarControls();
      this.handleSticky();
    };
    document.addEventListener("matchMobile", this._matchMobileHandler);
    document.addEventListener("unmatchMobile", this._unmatchMobileHandler);

    this.siteNav = new SiteNav(this);
    window.__sfHeader = this;

    this._resizeHandler = () => {
      if (this._resizeFrame) window.cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = window.requestAnimationFrame(() => {
        this._resizeFrame = null;
        this.innerWidth = window.innerWidth;
        this.refreshHeaderMetrics();
      });
    };
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  refreshHeaderMetrics() {
    const wrapper = this.domNodes && this.domNodes.headerWrapper;
    if (!wrapper) return;
    this.headerOffsetTop = wrapper.offsetTop;
    this.headerHeight = wrapper.offsetHeight;
    this._stickyExtraSpace = this.getStickyExtraSpace();
  }

  initAddon() {
    this.megamenu = new Megamenu(this);
    if (Shopify.designMode) {
      MinimogTheme = MinimogTheme || {};
      MinimogTheme && MinimogTheme.Wishlist && MinimogTheme.Wishlist.updateWishlistCount();
    }
  }

  getTopbar() {
    return (this.domNodes && this.domNodes.topbar) || this.querySelector(this.selectors.topbar);
  }

  getDirectChild(container, element) {
    if (!container || !element || !container.contains(element)) return null;

    let item = element;
    while (item.parentElement && item.parentElement !== container) {
      item = item.parentElement;
    }

    return item.parentElement === container ? item : null;
  }

  decorateTopbarControls() {
    const topbar = this.getTopbar();
    if (!topbar || !topbar.isConnected) return;

    // The top bar is permanent. Remove stale markup from an older snippet or
    // from a cached Theme Editor section render.
    topbar.querySelectorAll(".m-topbar__close").forEach((button) => button.remove());

    const topbarLeft = topbar.querySelector(".m-topbar__left");
    topbar.querySelectorAll('a[href^="tel:"]').forEach((phoneLink) => {
      phoneLink.classList.add("m-topbar__phone-link");
      const phoneItem = this.getDirectChild(topbarLeft, phoneLink);
      if (phoneItem) phoneItem.classList.add("m-topbar__mobile-phone-item");
    });

    const topbarRight = topbar.querySelector(".m-topbar__right");
    const languageField = topbar.querySelector(
      '[name="language_code"], [data-language-switcher], .m-language-switcher'
    );
    let languageItem = null;

    if (languageField) {
      const languageSwitcher =
        languageField.closest(".m-switcher-dropdown, localization-form, form") || languageField;
      languageItem = this.getDirectChild(topbarRight, languageSwitcher);

      if (languageItem) {
        languageItem.classList.add("m-topbar__mobile-language-item");
      }
    }

    const shouldShowMobileLanguage = this.dataset.mobileTopbarLanguage === "true";
    topbar.classList.toggle("m-topbar--has-language", Boolean(languageItem) && shouldShowMobileLanguage);

    // Shopify only includes languages enabled for the visitor's active Market
    // in localization.available_languages. Expose the state for safe styling
    // without inventing unavailable storefront routes.
    const languageCount = Number.parseInt(this.dataset.languageCount || "0", 10);
    topbar.classList.toggle("m-topbar--single-language", languageCount < 2);

    window.requestAnimationFrame(() => this.refreshHeaderMetrics());
  }

  initTopbarControls() {
    const topbar = this.getTopbar();
    if (!topbar) return;

    // The top-bar scheme is intentionally an inner-page setting. The homepage
    // keeps the transparent/gradient presentation configured by the header.
    if (this.dataset.pageType !== "index") {
      const colorScheme = this.dataset.topbarInnerColorScheme;

      if (colorScheme) {
        Array.from(topbar.classList).forEach((className) => {
          if (className.indexOf("m-color-") === 0) topbar.classList.remove(className);
        });

        topbar.classList.add("m-gradient", `m-color-${colorScheme}`);
      }
    }

    this.decorateTopbarControls();

    if (this._topbarObserver) this._topbarObserver.disconnect();
    this._topbarObserver = new MutationObserver(() => {
      if (this._topbarObserverFrame) return;
      this._topbarObserverFrame = window.requestAnimationFrame(() => {
        this._topbarObserverFrame = null;
        this.decorateTopbarControls();
      });
    });
    this._topbarObserver.observe(topbar, { childList: true, subtree: true });

    if (this._topbarElement && this._topbarClickHandler) {
      this._topbarElement.removeEventListener("click", this._topbarClickHandler);
    }
    if (this._documentClickHandler) {
      document.removeEventListener("click", this._documentClickHandler);
    }

    this._topbarElement = topbar;
    this._topbarClickHandler = (event) => {
      const languageControl = event.target.closest(
        ".m-topbar__mobile-language-item, .m-language-switcher, [data-language-switcher]"
      );
      if (languageControl && topbar.contains(languageControl)) {
        this.classList.add(this.classes.topbarLanguageOpen);
      }
    };
    this._documentClickHandler = (event) => {
      const languageControl = event.target.closest(
        ".m-topbar__mobile-language-item, .m-language-switcher, [data-language-switcher]"
      );
      if (!languageControl || !this.contains(languageControl)) {
        this.classList.remove(this.classes.topbarLanguageOpen);
      }
    };
    topbar.addEventListener("click", this._topbarClickHandler);
    document.addEventListener("click", this._documentClickHandler);
  }

  getStickyExtraSpace() {
    let extraSpace = 20;
    const sectionGroups = document.querySelectorAll(".shopify-section-group-header-group");

    sectionGroups.forEach((section) => {
      if (!section.classList.contains("m-section-header") && !section.classList.contains("m-section-scaling-logo")) {
        extraSpace += section.offsetHeight;
      }
    });

    const topBar = this.getTopbar() || document.querySelector(this.selectors.topbar);
    if (topBar) {
      const topbarStyles = window.getComputedStyle(topBar);
      const isVisible =
        topbarStyles.display !== "none" &&
        topbarStyles.visibility !== "hidden" &&
        topBar.getBoundingClientRect().height > 0;

      if (isVisible) {
        extraSpace +=
          topBar.offsetHeight +
          (parseFloat(topbarStyles.marginTop) || 0) +
          (parseFloat(topbarStyles.marginBottom) || 0);
      }
    }

    return extraSpace;
  }

  handleSticky() {
    if (this._stickyScrollHandler) {
      window.removeEventListener("scroll", this._stickyScrollHandler);
    }

    this.refreshHeaderMetrics();
    let lastScroll = window.scrollY;

    this._stickyScrollHandler = () => {
      const currentScroll = window.scrollY;
      const extraSpace = this._stickyExtraSpace;

      if (currentScroll <= extraSpace) {
        this.classList.remove("m-header--scrolled");
        this.classList.remove(
          this.classes.scrollUp,
          this.classes.scrollDown,
          this.classes.stuck,
          this.classes.always
        );
        document.body.classList.remove(this.classes.headerScrollUp, this.classes.headerScrollDown);
        lastScroll = currentScroll;
        return;
      }

      this.classList.add("m-header--scrolled");

      if (this.stickyHeader === "on_scroll_up") {
        this.classList.add(this.classes.stuck);
      } else if (this.stickyHeader === "always") {
        this.classList.add(this.classes.always);
      }

      if (
        currentScroll > this.headerHeight + extraSpace &&
        currentScroll > lastScroll &&
        !this.classList.contains(this.classes.scrollDown)
      ) {
        this.classList.remove(this.classes.scrollUp);
        document.body.classList.remove(this.classes.headerScrollUp);
        this.classList.add(this.classes.scrollDown);
        document.body.classList.add(this.classes.headerScrollDown);
      } else if (currentScroll < lastScroll && this.classList.contains(this.classes.scrollDown)) {
        this.classList.remove(this.classes.scrollDown);
        document.body.classList.remove(this.classes.headerScrollDown);
        this.classList.add(this.classes.scrollUp);
        document.body.classList.add(this.classes.headerScrollUp);
      }

      lastScroll = currentScroll;
    };

    window.addEventListener("scroll", this._stickyScrollHandler, { passive: true });
    this._stickyScrollHandler();
  }

  disconnectedCallback() {
    if (this._stickyScrollHandler) {
      window.removeEventListener("scroll", this._stickyScrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._resizeFrame) {
      window.cancelAnimationFrame(this._resizeFrame);
    }
    if (this._matchMobileHandler) {
      document.removeEventListener("matchMobile", this._matchMobileHandler);
    }
    if (this._unmatchMobileHandler) {
      document.removeEventListener("unmatchMobile", this._unmatchMobileHandler);
    }
    if (this._topbarObserver) {
      this._topbarObserver.disconnect();
    }
    if (this._topbarObserverFrame) {
      window.cancelAnimationFrame(this._topbarObserverFrame);
    }
    if (this._topbarElement && this._topbarClickHandler) {
      this._topbarElement.removeEventListener("click", this._topbarClickHandler);
    }
    if (this._documentClickHandler) {
      document.removeEventListener("click", this._documentClickHandler);
    }
  }
}

if (!customElements.get("m-header")) {
  customElements.define("m-header", MHeader);
}
